/**
 * PDF 뷰어 컴포넌트
 * react-pdf를 사용하여 PDF 파일을 표시
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import apiClient from '../../../core/http/axios';
import { attachmentService } from '../../services/attachmentService';

// PDF.js worker 설정
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ============================================================================
// Styled Components
// ============================================================================

const ViewerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--bg-primary, #fff);
`;

const ViewerToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  background: var(--bg-secondary, #f5f5f5);
  border-bottom: 1px solid var(--border-color, #e0e0e0);
  flex-shrink: 0;
`;

const ToolbarCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PageInfo = styled.span`
  font-size: 13px;
  color: var(--text-secondary, #666);
  min-width: 80px;
  text-align: center;
`;

const PageButton = styled.button`
  padding: 4px 8px;
  border: 1px solid var(--border-color, #e0e0e0);
  border-radius: 4px;
  background: var(--bg-primary, #fff);
  color: var(--text-primary, #333);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: var(--bg-secondary, #f5f5f5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 16px;
`;

const ZoomButton = styled.button`
  padding: 4px 8px;
  border: 1px solid var(--border-color, #e0e0e0);
  border-radius: 4px;
  background: var(--bg-primary, #fff);
  color: var(--text-primary, #333);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-secondary, #f5f5f5);
  }
`;

const ZoomInfo = styled.span`
  font-size: 12px;
  color: var(--text-secondary, #666);
  min-width: 50px;
  text-align: center;
`;

const ToolbarButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ToolbarButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  background: var(--bg-tertiary, #e0e0e0);
  color: var(--text-primary, #333);
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-quaternary, #d0d0d0);
  }
`;

const PdfContainer = styled.div<{ $isDragging: boolean }>`
  flex: 1;
  overflow: auto;
  padding: 0;
  background: #525659;
  cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
  user-select: none;

  .react-pdf__Page__textContent {
    pointer-events: none;
  }
`;

const LoadingOverlay = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary, #fff);
  color: var(--text-secondary, #666);
  font-size: 14px;
`;

const ErrorOverlay = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: var(--bg-primary, #fff);
  color: var(--text-secondary, #666);
  font-size: 14px;
`;

const StyledDocument = styled(Document)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  min-width: min-content;
  margin: 0 auto;
`;

const PageWrapper = styled.div`
  line-height: 0;
`;

// ============================================================================
// Props Interface
// ============================================================================

export interface PdfViewerProps {
  bookUid: string;
  fileUuid: string;
  fileName: string;
}

// ============================================================================
// Component
// ============================================================================

const PdfViewer: React.FC<PdfViewerProps> = ({
  bookUid,
  fileUuid,
  fileName,
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number } | null>(null);

  // PDF 파일 로드 (Blob URL 직접 사용 또는 API에서 로드)
  const loadPdf = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (fileUuid.startsWith('blob:')) {
        setPdfUrl(fileUuid);
        setLoading(false);
        return;
      }

      const url = attachmentService.getFileUrl(fileUuid);
      const response = await apiClient.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      setPdfUrl(blobUrl);
    } catch (err: any) {
      // console.error('[PdfViewer] PDF 로드 실패:', err);
      setError(err.message || 'PDF 파일을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [fileUuid]);

  useEffect(() => {
    loadPdf();
    return () => {
      setPdfUrl((prevUrl) => {
        if (prevUrl && prevUrl.startsWith('blob:') && !fileUuid.startsWith('blob:')) {
          URL.revokeObjectURL(prevUrl);
        }
        return null;
      });
    };
  }, [loadPdf, fileUuid]);

  // PDF 문서 로드 완료
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
    // console.log('[PdfViewer] 문서 로드 완료, 총 페이지:', numPages);
  };

  // 페이지 이동
  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  };

  // 줌
  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3.0));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setScale(1.0);
  };

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const container = containerRef.current;
    if (!container) return;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !dragStartRef.current) return;
    const container = containerRef.current;
    if (!container) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    container.scrollLeft = dragStartRef.current.scrollLeft - dx;
    container.scrollTop = dragStartRef.current.scrollTop - dy;
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      dragStartRef.current = null;
    }
  }, [isDragging]);

  // 터치 이벤트 (모바일/태블릿 드래그 + 스와이프 페이지 이동)
  const touchStartRef = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number; time: number } | null>(null);
  const isTouchScrollingRef = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) return;
    const touch = e.touches[0];
    if (!touch) return;
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
      time: Date.now(),
    };
    isTouchScrollingRef.current = false;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!touchStartRef.current) return;
    const container = containerRef.current;
    if (!container) return;
    const touch = e.touches[0];
    if (!touch) return;

    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;

    const hasScrollableContent =
      container.scrollHeight > container.clientHeight ||
      container.scrollWidth > container.clientWidth;

    if (hasScrollableContent) {
      container.scrollLeft = touchStartRef.current.scrollLeft - dx;
      container.scrollTop = touchStartRef.current.scrollTop - dy;
      isTouchScrollingRef.current = true;
      e.preventDefault();
      e.stopPropagation();
    } else if (Math.abs(dy) > 10) {
      isTouchScrollingRef.current = true;
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!touchStartRef.current) return;
    const touch = e.changedTouches[0];
    if (!touch) {
      touchStartRef.current = null;
      return;
    }

    const container = containerRef.current;
    const hasScrollableContent = container
      ? container.scrollHeight > container.clientHeight || container.scrollWidth > container.clientWidth
      : false;

    if (!hasScrollableContent && isTouchScrollingRef.current) {
      const dy = touch.clientY - touchStartRef.current.y;
      const duration = Date.now() - touchStartRef.current.time;
      if (Math.abs(dy) >= 30 && duration <= 800) {
        if (dy < 0) {
          setPageNumber(prev => Math.min(prev + 1, numPages));
        } else {
          setPageNumber(prev => Math.max(prev - 1, 1));
        }
      }
    }

    touchStartRef.current = null;
    isTouchScrollingRef.current = false;
  }, [numPages]);

  const wheelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.stopPropagation();
      e.preventDefault();

      const hasScrollableContent =
        container.scrollHeight > container.clientHeight ||
        container.scrollWidth > container.clientWidth;

      if (hasScrollableContent) {
        container.scrollTop += e.deltaY;
        container.scrollLeft += e.deltaX;
      } else {
        if (wheelTimerRef.current) return;
        if (e.deltaY > 0) {
          setPageNumber(prev => Math.min(prev + 1, numPages));
        } else if (e.deltaY < 0) {
          setPageNumber(prev => Math.max(prev - 1, 1));
        }
        wheelTimerRef.current = setTimeout(() => {
          wheelTimerRef.current = null;
        }, 300);
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [loading, error, numPages]);

  // bookUid는 향후 확장을 위해 유지
  void bookUid;

  if (loading) {
    return (
      <ViewerContainer>
        <LoadingOverlay>PDF를 불러오는 중...</LoadingOverlay>
      </ViewerContainer>
    );
  }

  if (error) {
    return (
      <ViewerContainer>
        <ErrorOverlay>
          <span>{error}</span>
          <ToolbarButton onClick={loadPdf}>다시 시도</ToolbarButton>
        </ErrorOverlay>
      </ViewerContainer>
    );
  }

  return (
    <ViewerContainer data-pdf-viewer>
      <ViewerToolbar>
        <ToolbarCenter>
          <PageButton onClick={goToPrevPage} disabled={pageNumber <= 1}>
            ◀
          </PageButton>
          <PageInfo>
            {pageNumber} / {numPages}
          </PageInfo>
          <PageButton onClick={goToNextPage} disabled={pageNumber >= numPages}>
            ▶
          </PageButton>
          
          <ZoomControls>
            <ZoomButton onClick={zoomOut}>−</ZoomButton>
            <ZoomInfo>{Math.round(scale * 100)}%</ZoomInfo>
            <ZoomButton onClick={zoomIn}>+</ZoomButton>
            <ZoomButton onClick={resetZoom}>맞춤</ZoomButton>
          </ZoomControls>
        </ToolbarCenter>

      </ViewerToolbar>

      <PdfContainer
        ref={containerRef}
        $isDragging={isDragging}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {pdfUrl && (
          <StyledDocument
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<LoadingOverlay>페이지 로딩 중...</LoadingOverlay>}
            error={<ErrorOverlay>PDF를 표시할 수 없습니다.</ErrorOverlay>}
          >
            <PageWrapper>
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            </PageWrapper>
          </StyledDocument>
        )}
      </PdfContainer>
    </ViewerContainer>
  );
};

export default PdfViewer;
